import React, { useEffect, useState, useMemo } from 'react';
import { Mail, Phone, Video, Building2, RefreshCw, MessageSquarePlus, Filter, Calendar, Clock, X, Send, Copy, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { copyToClipboard } from '../../utils/clipboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { value: 'new',        label: 'New',        badge: 'bg-blue-500/20 text-blue-300 border-blue-500/50' },
  { value: 'contacted',  label: 'Contacted',  badge: 'bg-amber-500/20 text-amber-300 border-amber-500/50' },
  { value: 'scheduled',  label: 'Scheduled',  badge: 'bg-purple-500/20 text-purple-300 border-purple-500/50' },
  { value: 'completed',  label: 'Completed',  badge: 'bg-green-500/20 text-green-300 border-green-500/50' },
  { value: 'declined',   label: 'Declined',   badge: 'bg-gray-500/20 text-gray-300 border-gray-500/50' },
  { value: 'converted',  label: 'Converted',  badge: 'bg-emerald-500/30 text-emerald-300 border-emerald-500/60' },
];

const ROLE_LABELS = {
  wealth_advisor: 'Wealth Advisor',
  family_office: 'Family Office',
  asset_manager: 'Asset Manager',
  other: 'Other',
};

const INTEREST_LABELS = {
  platform_overview: 'Platform overview',
  partner_introductions: 'Partner introductions',
  integration_capability: 'Integration capability',
  security_review: 'Security review',
  other: 'Other',
};

const CONTACT_ICON = { email: Mail, video_call: Video, phone: Phone };

const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const StatusBadge = ({ status }) => {
  const cfg = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
  return (
    <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${cfg.badge}`}>
      {cfg.label}
    </span>
  );
};

const BriefingDetailDrawer = ({ briefing, onClose, onUpdate, onConvert, busy }) => {
  const [noteDraft, setNoteDraft] = useState('');
  const [convertResult, setConvertResult] = useState(null);
  const [copied, setCopied] = useState(false);
  if (!briefing) return null;
  const ContactIcon = CONTACT_ICON[briefing.contact_channel] || Mail;
  const notes = briefing.admin_notes || [];
  const canConvert = briefing.status === 'completed';
  const isConverted = briefing.status === 'converted';

  const handleConvert = async () => {
    const result = await onConvert();
    if (result) setConvertResult(result);
  };

  const copyLink = async (url) => {
    const fullUrl = `${window.location.origin}${url}`;
    const ok = await copyToClipboard(fullUrl);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } else {
      toast.error('Could not copy — please select the link and copy manually');
    }
  };

  const conversionUrl = convertResult?.prefill_url || (briefing.conversion_token ? `/?invite=1&prefill=${briefing.conversion_token}` : null);

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="briefing-drawer">
      <button aria-label="Close" onClick={onClose} className="flex-1 bg-black/60" />
      <aside className="w-full max-w-xl bg-gray-900 border-l border-gray-700 overflow-y-auto">
        <div className="sticky top-0 bg-gray-900 border-b border-gray-700 p-5 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold text-white">{briefing.name}</h3>
            <p className="text-sm text-gray-400">{briefing.organisation}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-5">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Role</div>
              <div className="text-gray-200">{ROLE_LABELS[briefing.role] || briefing.role}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Interest</div>
              <div className="text-gray-200">{INTEREST_LABELS[briefing.interest_area] || briefing.interest_area}</div>
            </div>
            <div className="col-span-2">
              <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Contact</div>
              <div className="flex items-center gap-3 text-gray-200">
                <span className="flex items-center gap-1.5"><ContactIcon className="w-3.5 h-3.5 text-emerald-400" />{briefing.contact_channel}</span>
                <a href={`mailto:${briefing.email}`} className="text-emerald-400 underline hover:text-emerald-300">{briefing.email}</a>
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Received</div>
              <div className="text-gray-200">{fmtDateTime(briefing.created_at)}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Briefing ID</div>
              <div className="text-gray-400 font-mono text-xs">{briefing.briefing_id}</div>
            </div>
          </div>

          {briefing.notes && (
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
              <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">Their message</div>
              <p className="text-sm text-gray-200 whitespace-pre-wrap">{briefing.notes}</p>
            </div>
          )}

          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">Status</div>
            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS.map(s => (
                <button
                  key={s.value}
                  disabled={busy || briefing.status === s.value}
                  onClick={() => onUpdate({ status: s.value })}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition border
                    ${briefing.status === s.value
                      ? 'bg-emerald-600 text-white border-emerald-500'
                      : 'bg-gray-800 text-gray-300 border-gray-700 hover:bg-gray-700 hover:text-white'}`}
                  data-testid={`briefing-status-${s.value}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Convert to Invitation — only enabled on completed briefings */}
          <div className="border border-emerald-700/40 bg-emerald-900/10 rounded-lg p-4" data-testid="briefing-convert-section">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <h4 className="text-sm font-semibold text-emerald-300 flex items-center gap-2">
                  <Send className="w-3.5 h-3.5" />Convert to Invitation
                </h4>
                <p className="text-xs text-gray-400 mt-1">
                  Generate a pre-filled invitation link for this briefer. Email it to them personally.
                </p>
              </div>
              {!conversionUrl && (
                <Button
                  size="sm"
                  disabled={!canConvert || busy}
                  onClick={handleConvert}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs whitespace-nowrap"
                  data-testid="briefing-convert-btn"
                >
                  Convert
                </Button>
              )}
            </div>
            {!canConvert && !conversionUrl && (
              <p className="text-xs text-amber-400 mt-2">
                Mark the briefing as <span className="font-semibold">Completed</span> first.
              </p>
            )}
            {conversionUrl && (
              <div className="mt-3" data-testid="briefing-convert-result">
                <div className="text-xs text-gray-400 mb-1.5">Pre-filled invitation link (expires in 30 days):</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-gray-900 border border-gray-700 rounded px-3 py-2 text-[11px] text-emerald-300 break-all">
                    {window.location.origin}{conversionUrl}
                  </code>
                  <Button size="sm" onClick={() => copyLink(conversionUrl)}
                    className="bg-gray-700 hover:bg-gray-600 text-white text-xs whitespace-nowrap"
                    data-testid="briefing-copy-link-btn">
                    {copied ? <><CheckCircle2 className="w-3 h-3 mr-1" />Copied</> : <><Copy className="w-3 h-3 mr-1" />Copy</>}
                  </Button>
                </div>
                {isConverted && (
                  <p className="text-xs text-gray-500 mt-2 italic">
                    Status set to <span className="text-emerald-400 font-semibold">Converted</span>. Email the link to {briefing.email}.
                  </p>
                )}
              </div>
            )}
          </div>

          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">Private notes</div>
            <div className="space-y-2 mb-3">
              {notes.length === 0 ? (
                <p className="text-xs text-gray-500 italic">No notes yet.</p>
              ) : notes.map((n, i) => (
                <div key={i} className="bg-gray-800 border border-gray-700 rounded p-3" data-testid={`briefing-note-${i}`}>
                  <p className="text-sm text-gray-200 whitespace-pre-wrap">{n.note}</p>
                  <p className="text-[10px] text-gray-500 mt-1.5">
                    {n.added_by} · {fmtDateTime(n.added_at)}
                  </p>
                </div>
              ))}
            </div>
            <Textarea
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value.slice(0, 800))}
              placeholder="Add a private note (only visible to admins)…"
              rows={3}
              className="text-sm bg-gray-800 border-gray-700 text-white"
              data-testid="briefing-note-input"
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-gray-500">{noteDraft.length}/800</span>
              <Button
                size="sm"
                disabled={!noteDraft.trim() || busy}
                onClick={() => { onUpdate({ admin_note: noteDraft.trim() }); setNoteDraft(''); }}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                data-testid="briefing-add-note-btn"
              >
                <MessageSquarePlus className="w-3 h-3 mr-1.5" />Add note
              </Button>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
};

const BriefingsAdminTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const t = localStorage.getItem('auth_token') || localStorage.getItem('adminToken') || localStorage.getItem('token');
      const r = await fetch(`${BACKEND_URL}/api/admin/briefings`, { headers: { Authorization: `Bearer ${t}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setRows(d.rows || []);
    } catch (e) {
      toast.error('Could not load briefings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return rows;
    return rows.filter(r => (r.status || 'new') === statusFilter);
  }, [rows, statusFilter]);

  const counts = useMemo(() => {
    const c = { all: rows.length };
    for (const s of STATUS_OPTIONS) c[s.value] = rows.filter(r => (r.status || 'new') === s.value).length;
    return c;
  }, [rows]);

  const updateBriefing = async (payload) => {
    if (!selected) return;
    setBusy(true);
    try {
      const t = localStorage.getItem('auth_token') || localStorage.getItem('adminToken') || localStorage.getItem('token');
      const r = await fetch(`${BACKEND_URL}/api/admin/briefings/${selected.briefing_id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Could not update briefing');
      }
      const data = await r.json();
      setSelected(data.briefing);
      setRows(prev => prev.map(p => p.briefing_id === data.briefing.briefing_id ? data.briefing : p));
      toast.success(payload.status ? `Status set to ${payload.status}` : 'Note added');
    } catch (e) {
      toast.error(e.message || 'Could not update briefing');
    } finally {
      setBusy(false);
    }
  };

  const convertBriefing = async () => {
    if (!selected) return null;
    setBusy(true);
    try {
      const t = localStorage.getItem('auth_token') || localStorage.getItem('adminToken') || localStorage.getItem('token');
      const r = await fetch(`${BACKEND_URL}/api/admin/briefings/${selected.briefing_id}/convert`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' },
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Could not convert briefing');
      }
      const data = await r.json();
      setSelected(data.briefing);
      setRows(prev => prev.map(p => p.briefing_id === data.briefing.briefing_id ? data.briefing : p));
      toast.success('Conversion link generated — copy and email it to the briefer.');
      return data;
    } catch (e) {
      toast.error(e.message || 'Could not convert briefing');
      return null;
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="briefings-admin-tab">
      <div className="bg-gray-900 rounded-xl p-3 sm:p-4 border border-gray-800 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-4 h-4 text-gray-400" />
          {[{ value: 'all', label: 'All' }, ...STATUS_OPTIONS].map(opt => (
            <button
              key={opt.value}
              onClick={() => setStatusFilter(opt.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition
                ${statusFilter === opt.value
                  ? 'bg-emerald-600 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
              data-testid={`briefings-filter-${opt.value}`}
            >
              {opt.label}{counts[opt.value] != null && <span className="ml-1.5 text-[10px] opacity-70">{counts[opt.value]}</span>}
            </button>
          ))}
        </div>
        <Button onClick={load} disabled={loading} variant="outline"
          className="bg-gray-800 border-gray-700 hover:bg-gray-700 text-white" data-testid="briefings-refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh
        </Button>
      </div>

      <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
        {loading && rows.length === 0 ? (
          <div className="text-center py-12 text-gray-400"><RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400" data-testid="briefings-empty">
            <Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No briefing requests in this view.</p>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="briefings-table">
            <thead className="bg-gray-800/60">
              <tr>
                {['Received', 'Name & Org', 'Role', 'Interest', 'Contact', 'Status'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {filtered.map(b => {
                const ContactIcon = CONTACT_ICON[b.contact_channel] || Mail;
                return (
                  <tr key={b.briefing_id} onClick={() => setSelected(b)}
                    className="hover:bg-gray-800/50 cursor-pointer" data-testid={`briefings-row-${b.briefing_id}`}>
                    <td className="px-4 py-3 text-gray-300">
                      <div className="flex items-center gap-1.5"><Calendar className="w-3 h-3 text-gray-500" />{fmtDateTime(b.created_at)}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-white font-medium">{b.name}</div>
                      <div className="text-xs text-gray-400">{b.organisation}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-300">{ROLE_LABELS[b.role] || b.role}</td>
                    <td className="px-4 py-3 text-gray-300">{INTEREST_LABELS[b.interest_area] || b.interest_area}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-gray-300">
                        <ContactIcon className="w-3.5 h-3.5 text-emerald-400" />
                        <span className="text-xs">{b.email}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={b.status || 'new'} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <BriefingDetailDrawer
        briefing={selected}
        onClose={() => setSelected(null)}
        onUpdate={updateBriefing}
        onConvert={convertBriefing}
        busy={busy}
      />
    </div>
  );
};

export default BriefingsAdminTab;
